/**
 * CommandService - Manages global commands and operations
 * Acts as a central coordinator for operations like cancelling streaming responses
 */

export type CommandType = "OPERATION_STOPPED" | "OPERATION_STARTED";

export interface CommandListener {
  (command: CommandType, data?: unknown): void;
}

export class CommandService {
  private abortController: AbortController | null = null;
  private subscribers: Set<CommandListener> = new Set();

  /**
   * Start a new operation with a fresh AbortController
   * @returns AbortSignal for the operation
   */
  startOperation(): AbortSignal {
    // Cancel any existing operation first
    this.stopOperation();

    this.abortController = new AbortController();
    this.notifyAll("OPERATION_STARTED");

    return this.abortController.signal;
  }

  /**
   * Stop the current operation by aborting the controller
   */
  stopOperation(): void {
    if (this.abortController) {
      this.abortController.abort("User cancelled the execution");
      this.abortController = null;
      this.notifyAll("OPERATION_STOPPED");
    }
  }

  /**
   * Subscribe to command notifications
   * @param callback Function to call when commands are issued
   * @returns Unsubscribe function
   */
  subscribe(callback: CommandListener): () => void {
    this.subscribers.add(callback);

    return () => this.subscribers.delete(callback);
  }

  /**
   * Notify all subscribers of a command
   * @param command The command being issued
   * @param data Optional data to pass with the command
   */
  notifyAll(command: CommandType, data?: unknown): void {
    this.subscribers.forEach((callback) => callback(command, data));
  }

  /**
   * Check if an operation is currently in progress
   */
  isOperationInProgress(): boolean {
    return this.abortController !== null;
  }
}

// Singleton instance to be used throughout the application
export const commandService = new CommandService();
